import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Receipt, Plus, X, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";

const blank = { payment_date: "", amount: "", method: "", period_covered: "", notes: "" };

export default function RentLedgerTab({ caseId }) {
 const [data, setData] = useState({ payments: [], total_paid: 0, year_total: 0, year: 0, count: 0 });
 const [adding, setAdding] = useState(false);
 const [form, setForm] = useState(blank);

 const load = () => api.get(`/cases/${caseId}/rent-payments`).then(({ data }) => setData(data));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 useEffect(() => { load(); }, [caseId]);

 const submit = async (e) => {
 e.preventDefault();
 try {
 await api.post(`/cases/${caseId}/rent-payments`, {
 payment_date: form.payment_date,
 amount: parseFloat(form.amount),
 method: form.method || null,
 period_covered: form.period_covered || null,
 notes: form.notes || null,
 });
 setForm(blank); setAdding(false); load();
 toast.success("Rent payment recorded", { description: `$${form.amount} on ${form.payment_date}` });
 } catch (e) { toast.error("Add failed", { description: e.response?.data?.detail || "Try again" }); }
 };

 const remove = async (id) => {
 if (!window.confirm("Delete this payment?")) return;
 await api.delete(`/rent-payments/${id}`);
 load();
 toast.success("Payment removed");
 };

 return (
 <div className="space-y-6" data-testid="rent-ledger-tab">
 <div className="card bg-[#E6F5F2]/40">
 <div className="flex items-start gap-3">
 <Receipt size={20} strokeWidth={1.5} className="text-[#1B6C62] mt-0.5" />
 <div className="flex-1">
 <h3 className="font-serif text-lg text-[#0D2B52] mb-1">Rent ledger</h3>
 <p className="text-sm text-[#333] leading-relaxed">
 Log every rent payment as you make it. Multi-year totals stay with you across tenancies. Useful for proving payment history,
 budgeting and demonstrating tenancy length at tribunal.
 </p>
 </div>
 </div>
 </div>

 <div className="grid sm:grid-cols-3 gap-4">
 <div className="card text-center">
 <div className="label-mono mb-2">Total paid</div>
 <div className="font-serif text-3xl text-[#0D2B52]">${data.total_paid.toLocaleString()}</div>
 <div className="text-xs text-[#6B7280] mt-1">{data.count} payment{data.count === 1 ? "" : "s"}</div>
 </div>
 <div className="card text-center">
 <div className="label-mono mb-2">{data.year}</div>
 <div className="font-serif text-3xl text-[#1B6C62]">${data.year_total.toLocaleString()}</div>
 <div className="text-xs text-[#6B7280] mt-1">This calendar year</div>
 </div>
 <button onClick={() => setAdding(true)} className="card card-hover text-center inline-flex items-center justify-center gap-2 text-[#0D2B52] font-medium" data-testid="add-payment-btn">
 <Plus size={18} strokeWidth={1.5} /> Log a payment
 </button>
 </div>

 {adding && (
 <form onSubmit={submit} className="card space-y-3" data-testid="payment-form">
 <div className="flex justify-between items-center">
 <h4 className="font-serif text-lg">New payment</h4>
 <button type="button" onClick={() => { setAdding(false); setForm(blank); }} className="btn-ghost p-1.5"><X size={18} strokeWidth={1.5} /></button>
 </div>
 <div className="grid sm:grid-cols-2 gap-3">
 <div><label className="label-mono block mb-1.5">Payment date</label>
 <input required type="date" className="input-field" value={form.payment_date} onChange={e=>setForm({...form,payment_date:e.target.value})} data-testid="payment-date" /></div>
 <div><label className="label-mono block mb-1.5">Amount ($)</label>
 <input required type="number" step="0.01" className="input-field" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} data-testid="payment-amount" /></div>
 <div><label className="label-mono block mb-1.5">Method</label>
 <input className="input-field" placeholder="Bank transfer, BPAY…" value={form.method} onChange={e=>setForm({...form,method:e.target.value})} /></div>
 <div><label className="label-mono block mb-1.5">Period covered</label>
 <input className="input-field" placeholder="Week of 14 Jun 2026" value={form.period_covered} onChange={e=>setForm({...form,period_covered:e.target.value})} /></div>
 </div>
 <div><label className="label-mono block mb-1.5">Notes</label>
 <textarea rows={2} className="input-field" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} /></div>
 <div className="flex justify-end gap-2">
 <button type="button" onClick={() => { setAdding(false); setForm(blank); }} className="btn-secondary text-sm">Cancel</button>
 <button type="submit" className="btn-primary text-sm" data-testid="submit-payment">Log payment</button>
 </div>
 </form>
 )}

 {data.payments.length === 0 ? (
 <div className="text-sm text-[#6B7280] italic text-center py-8">No payments logged yet. Receipts from your agent's emails can be uploaded under Documents. Log the amount here for the running total.</div>
 ) : (
 <div className="card p-0 overflow-hidden">
 <table className="w-full text-sm" data-testid="payments-table">
 <thead className="bg-[#FAF9F6] border-b border-[#E5E4DE]">
 <tr className="text-left">
 <th className="px-4 py-3 label-mono">Date</th>
 <th className="px-4 py-3 label-mono">Amount</th>
 <th className="px-4 py-3 label-mono">Method</th>
 <th className="px-4 py-3 label-mono">Period</th>
 <th className="px-4 py-3"></th>
 </tr>
 </thead>
 <tbody>
 {data.payments.map(p => (
 <tr key={p.id} className="border-b border-[#E5E4DE] last:border-0" data-testid={`payment-${p.id}`}>
 <td className="px-4 py-3 font-mono text-[#1A1A1A]">{p.payment_date}</td>
 <td className="px-4 py-3 font-mono text-[#0D2B52]">${Number(p.amount).toLocaleString()}</td>
 <td className="px-4 py-3 text-[#6B7280]">{p.method || ","}</td>
 <td className="px-4 py-3 text-[#6B7280]">{p.period_covered || ","}</td>
 <td className="px-4 py-3 text-right">
 <button onClick={() => remove(p.id)} className="btn-ghost p-1.5 hover:text-[#B35D4C]" data-testid={`delete-payment-${p.id}`}><Trash2 size={14} strokeWidth={1.5} /></button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 );
}
